import {
  CAPTURE_VICTORY_COUNT,
  FIELD_LIMIT_BY_GRADE,
  HOME_INCOME,
  MULTI_BUILD_REQUIRED_COINS,
  MULTI_INCOME,
  TECH_CARD_DEFINITIONS,
  UNIT_DEFINITIONS,
} from "../data";
import type {
  BoardTile,
  Coordinate,
  GameState,
  Grade,
  PlayerId,
  PlayerState,
  TechCardId,
  UnitDefinition,
  UnitInstance,
  UnitTypeId,
} from "../types";

export function cellKey(position: Coordinate): string {
  return `${position.x},${position.y}`;
}

export function samePosition(a: Coordinate, b: Coordinate): boolean {
  return a.x === b.x && a.y === b.y;
}

export function distance(a: Coordinate, b: Coordinate): number {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

export function findTile(
  board: readonly BoardTile[],
  position: Coordinate,
): BoardTile | undefined {
  return board.find((tile) => samePosition(tile.position, position));
}

export function getPlayer(
  state: GameState,
  playerId: PlayerId,
): PlayerState | undefined {
  return state.players.find((player) => player.id === playerId);
}

export function getOpponent(
  state: GameState,
  playerId: PlayerId,
): PlayerState | undefined {
  return state.players.find((player) => player.id !== playerId);
}

export function getUnitDefinition(unit: UnitInstance): UnitDefinition {
  return UNIT_DEFINITIONS[unit.typeId];
}

export function revealedTechIds(player: PlayerState): readonly TechCardId[] {
  return player.ownedTech
    .filter((tech) => tech.revealed)
    .map((tech) => tech.id);
}

export function hasRevealedTech(
  player: PlayerState | undefined,
  techCardId: TechCardId,
): boolean {
  return (
    player?.ownedTech.some((tech) => tech.id === techCardId && tech.revealed) ??
    false
  );
}

export interface ResolvedUnitStats {
  readonly level: number;
  readonly hp: number;
  readonly atk: number;
  readonly mov: number;
  readonly cost: number;
}

export function resolveUnitStats(
  state: GameState,
  unitOrTypeId: UnitInstance | UnitTypeId,
): ResolvedUnitStats {
  const typeId =
    typeof unitOrTypeId === "string" ? unitOrTypeId : unitOrTypeId.typeId;
  const unit = UNIT_DEFINITIONS[typeId];
  const owner =
    typeof unitOrTypeId === "string"
      ? undefined
      : getPlayer(state, unitOrTypeId.ownerId);
  const revealed = new Set(owner ? revealedTechIds(owner) : []);

  let hp = unit.hp;
  let atk = unit.atk;
  let level = unit.level;
  const mov = unit.mov;

  if (typeId === "WORKER" && revealed.has("ECSU_GRASSROOTS")) atk += 1;
  if (typeId === "TURTLE" && revealed.has("ECSU_BLOOD_CURTAIN")) level += 3;
  if (typeId === "TURTLE" && revealed.has("ECSU_TURTLE_SHIP")) hp += 2;
  if (typeId === "INVOLVE" && revealed.has("ECSU_MONGMONG")) hp += 5;
  if (typeId === "TOWER" && revealed.has("ECSU_DARK_TOWER")) atk += 2;
  if (typeId === "ANT" && revealed.has("SB_SPICY_PEPPER")) atk += 1;
  if (typeId === "SHELL" && revealed.has("SB_SCARY_SHELL")) hp += 2;
  if (typeId === "DESTROYER" && revealed.has("SB_DESTROY_ALL")) hp += 3;
  if (typeId === "SNEAKY" && revealed.has("SB_AMBUSH")) atk += 2;
  if (typeId === "DEFY" && revealed.has("GO_MAINTENANCE")) atk += 1;
  if (typeId === "DEFY" && revealed.has("GO_UNFINISHED_REVOLUTION")) atk += 1;
  if (typeId === "SONIC" && revealed.has("GO_FORCE_EQUALS_MA")) atk += 1;
  if (typeId === "BANDIT" && revealed.has("GO_HEAVY_ARMOR")) hp += 3;

  if (
    typeId === "DEFY" &&
    revealed.has("GO_FLY_UP") &&
    typeof unitOrTypeId !== "string"
  ) {
    const hasLatencySameCell = state.units.some(
      (candidate) =>
        candidate.ownerId === unitOrTypeId.ownerId &&
        candidate.typeId === "LATENCY" &&
        samePosition(candidate.position, unitOrTypeId.position),
    );
    if (hasLatencySameCell) level += 1;
  }

  return { level, hp, atk, mov, cost: unit.cost };
}

export function getUnitsAt(
  state: GameState,
  position: Coordinate,
): readonly UnitInstance[] {
  return state.units.filter((unit) => samePosition(unit.position, position));
}

export function getUnitsForPlayer(
  state: GameState,
  playerId: PlayerId,
): readonly UnitInstance[] {
  return state.units.filter((unit) => unit.ownerId === playerId);
}

export function getHomeTilesForPlayer(
  state: GameState,
  playerId: PlayerId,
): readonly BoardTile[] {
  return state.board.filter(
    (tile) => tile.kind === "home" && tile.homeOwnerId === playerId,
  );
}

export function getPrimaryHomeTile(
  state: GameState,
  playerId: PlayerId,
): BoardTile {
  const home = getHomeTilesForPlayer(state, playerId)[0];
  if (!home) {
    throw new Error(`Home tile not found for player: ${playerId}`);
  }
  return home;
}

export function countControlledCapturePoints(
  state: GameState,
  playerId: PlayerId,
): number {
  return state.board.filter(
    (tile) =>
      tile.kind === "capture" &&
      state.controlByCell[cellKey(tile.position)] === playerId,
  ).length;
}

export function ownsOpponentHome(
  state: GameState,
  playerId: PlayerId,
): boolean {
  return state.board.some(
    (tile) =>
      tile.kind === "home" &&
      tile.homeOwnerId !== playerId &&
      state.controlByCell[cellKey(tile.position)] === playerId,
  );
}

export function getWinnerByControl(
  state: GameState,
): { winnerId: PlayerId; condition: "capture_points" | "home_capture" } | null {
  for (const player of state.players) {
    if (
      countControlledCapturePoints(state, player.id) >= CAPTURE_VICTORY_COUNT
    ) {
      return { winnerId: player.id, condition: "capture_points" };
    }
    if (ownsOpponentHome(state, player.id)) {
      return { winnerId: player.id, condition: "home_capture" };
    }
  }
  return null;
}

export function isMultiActive(state: GameState, position: Coordinate): boolean {
  return (
    (state.constructionByCell[cellKey(position)] ?? 0) >=
    MULTI_BUILD_REQUIRED_COINS
  );
}

export function calculateIncome(state: GameState, playerId: PlayerId): number {
  let income = 0;
  const controlledOwnHome = state.board.some(
    (tile) =>
      tile.kind === "home" &&
      tile.homeOwnerId === playerId &&
      state.controlByCell[cellKey(tile.position)] === playerId,
  );
  if (controlledOwnHome) income += HOME_INCOME;

  for (const tile of state.board) {
    const controller = state.controlByCell[cellKey(tile.position)];
    if (controller !== playerId) continue;
    if (tile.kind === "capture") income += tile.income;
    if (tile.kind === "multi" && isMultiActive(state, tile.position)) {
      income += MULTI_INCOME;
    }

    const shellBonus = state.units.some(
      (unit) =>
        unit.ownerId === playerId &&
        unit.typeId === "SHELL" &&
        samePosition(unit.position, tile.position) &&
        (tile.kind === "home" ||
          tile.kind === "multi" ||
          tile.kind === "capture"),
    );
    if (shellBonus) income += 1;
  }

  return income;
}

export function countFieldedUnitType(
  state: GameState,
  playerId: PlayerId,
  unitTypeId: UnitTypeId,
): number {
  return state.units.filter(
    (unit) => unit.ownerId === playerId && unit.typeId === unitTypeId,
  ).length;
}

export function fieldLimitForUnit(unitTypeId: UnitTypeId): number {
  return FIELD_LIMIT_BY_GRADE[UNIT_DEFINITIONS[unitTypeId].grade];
}

export function legalDestinationsForUnit(
  state: GameState,
  unitId: string,
): readonly Coordinate[] {
  const unit = state.units.find((candidate) => candidate.id === unitId);
  if (!unit) return [];
  const stats = resolveUnitStats(state, unit);
  if (stats.mov <= 0) return [];
  return state.board
    .filter(
      (tile) =>
        tile.kind !== "blocked" &&
        distance(unit.position, tile.position) <= stats.mov &&
        !samePosition(unit.position, tile.position),
    )
    .map((tile) => tile.position);
}

export function canProduceStructureAtWorker(
  state: GameState,
  playerId: PlayerId,
  workerUnitId: string | undefined,
): boolean {
  if (!workerUnitId) return false;
  const worker = state.units.find(
    (unit) =>
      unit.id === workerUnitId &&
      unit.ownerId === playerId &&
      unit.typeId === "WORKER",
  );
  return worker !== undefined;
}

export function hasOwnedMidTech(player: PlayerState): boolean {
  return player.ownedTech.some(
    (owned) => TECH_CARD_DEFINITIONS[owned.id].grade === "mid",
  );
}

export function hasOwnedAdvancedTech(player: PlayerState): boolean {
  return player.ownedTech.some(
    (owned) => TECH_CARD_DEFINITIONS[owned.id].grade === "adv",
  );
}

export function canProduceGrade(player: PlayerState, grade: Grade): boolean {
  if (grade === "basic") return true;
  if (grade === "mid") return hasOwnedMidTech(player);
  return hasOwnedAdvancedTech(player);
}
